"use client";

import React, { useEffect, useRef, useState } from "react";
import { Briefcase, Wine, Heart } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const pillars = [{
  title: "Corporate",
  description: "Elevating brand experiences through meticulously planned corporate events, galas, and executive retreats.",
  icon: Briefcase
}, {
  title: "Private",
  description: "Curating exclusive milestones, from intimate anniversary dinners to lavish birthday soirées.",
  icon: Wine
}, {
  title: "Weddings",
  description: "Designing breathtaking matrimonial celebrations that reflect your unique love story with flawless execution.",
  icon: Heart
}];
export default function ArtOfTheSoiree() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="art-of-the-soiree" className="bg-background py-24 md:py-32 px-6">
      <div ref={ref} className={`max-w-6xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center mb-20">
          <h2 className="font-serif text-4xl md:text-5xl text-foreground mb-6 tracking-tight">
            The Art of the Soirée
          </h2>
          <div className="w-12 h-[1px] bg-foreground/20 mx-auto"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <div key={pillar.title} data-index={i} className="flex flex-col items-center text-center group">
                <div className="mb-8 text-foreground transition-transform duration-500 ease-out group-hover:scale-105">
                  <Icon className="w-12 h-12" strokeWidth={1} />
                </div>
                <h3 className="font-serif text-2xl text-foreground mb-4 tracking-wide">
                  {pillar.title}
                </h3>
                <p className="font-sans font-light text-muted-foreground leading-relaxed line-clamp-3 max-w-[280px]">
                  {pillar.description}
                </p>
              </div>;
        })}
        </div>
      </div>
    </section>;
}